import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { z } from "zod";
import { CheckboxInput, Typography } from "@/components/atoms";
import { ActiveStatuses, ArchivedStatuses, PendingStatuses, SuspendedStatuses } from "../../../types";
import { FilterDrawer } from "../common/FilterDrawer";
import { FilterPopover } from "../common/FilterPopover";
import { useExternalSuppliersFilters } from "../useExternalSuppliersFilters";

const schema = z.object({
  status: z.array(z.string()),
});

type FormData = z.infer<typeof schema>;

export const StatusFilter = () => {
  const {
    filters: { status },
    setFilter,
  } = useExternalSuppliersFilters();

  const value = useMemo(() => {
    if (!status) return undefined;

    return status.filter(Boolean).join(", ");
  }, [status]);

  const onClose = () => setFilter("status", undefined);

  return (
    <FilterPopover name="status" value={value} label="Status" onClose={onClose}>
      <StatusFilterForm />
    </FilterPopover>
  );
};

interface StatusFilterDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const StatusFilterDrawer = ({ open, onOpenChange }: StatusFilterDrawerProps) => {
  const {
    filters: { status },
    setFilter,
  } = useExternalSuppliersFilters();

  const value = useMemo(() => {
    if (!status) return undefined;

    return status.filter(Boolean).join(", ");
  }, [status]);

  const onClose = () => setFilter("status", undefined);

  return (
    <FilterDrawer open={open} onOpenChange={onOpenChange} value={value} label="Status" onClose={onClose}>
      <StatusFilterForm />
    </FilterDrawer>
  );
};

const StatusFilterForm = () => {
  const { pathname } = useLocation();
  const { setFilter, filters } = useExternalSuppliersFilters();
  const isArchivedExternalSuppliers = pathname.includes("/external-suppliers/archived-external-suppliers");
  const statusList = isArchivedExternalSuppliers ? ArchivedStatuses : [...ActiveStatuses, ...PendingStatuses, ...SuspendedStatuses];
  const options = statusList.map((status) => ({ name: status, value: status }));

  const { control, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { status: (filters.status as string[]) ?? [] },
  });

  const handleCheckAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      if (checked) {
        setFilter(
          "status",
          options.map((status) => status.value)
        );
        setValue(
          "status",
          options.map((status) => status.value)
        );
      } else {
        setFilter("status", null);
        setValue("status", []);
      }
    },
    [setFilter, setValue, options]
  );

  const isAllStatusesSelected = useMemo(() => {
    if (!filters.status) return false;
    return filters.status.length === options.length;
  }, [filters.status, options]);

  return (
    <div className="contents">
      <Typography className="mb-4">Status</Typography>
      <CheckboxInput label="All Statuses" onChange={handleCheckAll} variant="check" checked={isAllStatusesSelected} />
      <div className="mt-4 grid gap-4 ">
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <>
              {options.map((status) => (
                <CheckboxInput
                  className="capitalize"
                  key={status.value}
                  label={status.name}
                  value={status.value}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newStatus = [...field.value, e.target.value];
                      field.onChange(newStatus);
                      setFilter("status", newStatus.length > 0 ? newStatus : null);
                    } else {
                      const newStatus = field.value.filter((i) => i !== e.target.value);
                      field.onChange(newStatus);
                      setFilter("status", newStatus.length > 0 ? newStatus : null);
                    }
                  }}
                  variant="check"
                  checked={field.value.includes(status.value)}
                />
              ))}
            </>
          )}
        />
      </div>
    </div>
  );
};
